import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Play, Pause, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Eye, EyeOff, Download, Lock, Unlock, Coins, Volume2,
  SkipForward, BookOpen, Rewind, FastForward, Share2,
  Twitter, Facebook, MessageCircle, Mail, Link2, Check, Copy
} from "lucide-react";
import { useAudio } from "../contexts/AudioContext";
import { useTheme } from "../contexts/ThemeContext";
import { useAdmin } from "../contexts/AdminContext";
import { API, BACKEND_URL, COVER_IMAGE, getUserToken } from "../lib/constants";

const SERIES_LABELS = {
  'the-red-ink-chronicles': 'The Red Ink Chronicles',
  'signed-sealed-betrayed': 'Signed, Sealed, Betrayed',
  'the-notarys-shadow': "The Notary's Shadow",
};
const seriesLabelFor = (slug) => SERIES_LABELS[slug] || slug;

export default function EpisodeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStory, setShowStory] = useState(true);
  const { isDark } = useTheme();
  const { isAdmin } = useAdmin();
  const {
    autoPlayNext, toggleAutoPlayNext,
    currentTrack, isPlaying, progress: ctxProgress, duration: ctxDuration,
    playbackRate, playTrack, togglePlay, seek, skipBy: ctxSkipBy,
    cyclePlaybackRate, setOnTrackEnd, setNextTrack, getPosition,
  } = useAudio();

  const [adjacentEps, setAdjacentEps] = useState({ prev: null, next: null });

  // Access gating
  const [hasAccess, setHasAccess] = useState(false);
  const [freeEpisodesUsed, setFreeEpisodesUsed] = useState([]);
  const [isGated, setIsGated] = useState(false);
  const [creditCost, setCreditCost] = useState(1);
  const [wallet, setWallet] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [canClaimDaily, setCanClaimDaily] = useState(false);
  const FREE_LIMIT = 25;

  // UI-only state
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copyOk, setCopyOk] = useState(false);
  const [storyCopied, setStoryCopied] = useState(false);
  const [skipFlash, setSkipFlash] = useState(null);

  // Is THIS page's episode the one currently playing in the global engine?
  const isThisPlaying = currentTrack?.id === episode?.id;
  const currentTime = isThisPlaying ? ctxProgress : 0;
  const audioDuration = isThisPlaying ? ctxDuration : 0;
  const localIsPlaying = isThisPlaying && isPlaying;

  useEffect(() => {
    const fetchEpisode = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/episodes/${id}`);
        setEpisode(res.data);
        const seriesSlug = res.data.series || 'the-red-ink-chronicles';
        setCreditCost(1); // $0.10 per episode, all series
        localStorage.setItem('lastReadEpisode', JSON.stringify({
          id: res.data.id, title: res.data.title, season: res.data.season,
          episode_number: res.data.episode_number, thumbnail_url: res.data.thumbnail_url,
          series: res.data.series, timestamp: Date.now()
        }));
        const adjRes = await axios.get(`${API}/episodes/${id}/adjacent`);
        setAdjacentEps(adjRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchEpisode();
  }, [id]);

  // Build a track object from episode for the global audio engine
  const buildTrack = (ep) => {
    if (!ep) return null;
    const audioUrl = ep.mixed_audio_url || ep.audio_url;
    if (!audioUrl) return null;
    return {
      id: ep.id,
      audio_url: `${BACKEND_URL}${audioUrl}`,
      title: ep.title,
      subtitle: `${seriesLabelFor(ep.series)} · S${ep.season}E${ep.episode_number}`,
      thumbnail_url: ep.thumbnail_url,
      season: ep.season,
      episode_number: ep.episode_number,
      route: `/episodes/${ep.id}`,
    };
  };

  // Auto-play next handler — when track ends, navigate to next episode page (global engine already starts the next track)
  const handleTrackEnd = useCallback((nextPlayed) => {
    // nextPlayed is the track auto-engine kicked off (or null if none)
    if (nextPlayed && nextPlayed.route) {
      try { sessionStorage.setItem('ris_autoplay_pending', '1'); } catch {}
      navigate(nextPlayed.route);
    }
  }, [navigate]);

  useEffect(() => {
    setOnTrackEnd(() => handleTrackEnd);
    return () => setOnTrackEnd(null);
  }, [handleTrackEnd, setOnTrackEnd]);

  // When this episode page becomes the "current" target and access is unlocked,
  // tell the engine which track is next-up (so autoplay-chain works)
  useEffect(() => {
    if (!episode) return;
    const nextEp = adjacentEps.next ? buildTrack(adjacentEps.next) : null;
    setNextTrack(nextEp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.id, adjacentEps.next?.id]);

  // If user navigated here because of autoplay-pending, kick off playback once access is granted
  useEffect(() => {
    if (!episode || !hasAccess || isGated) return;
    let pending = false;
    try { pending = sessionStorage.getItem('ris_autoplay_pending') === '1'; } catch {}
    if (!pending) return;
    try { sessionStorage.removeItem('ris_autoplay_pending'); } catch {}
    const t = buildTrack(episode);
    const next = adjacentEps.next ? buildTrack(adjacentEps.next) : null;
    if (t) playTrack(t, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.id, hasAccess, isGated, adjacentEps.next?.id]);

  useEffect(() => {
    const checkAccess = async () => {
      const userToken = getUserToken();
      try {
        const wRes = await axios.get(`${API}/store/wallet/${userToken}`);
        setWallet(wRes.data);
        if (wRes.data.unlocked_episodes?.includes(id) || wRes.data.daily_free_episodes?.includes(id)) {
          setHasAccess(true);
          return;
        }
        const lastFree = wRes.data.last_daily_free_at;
        if (lastFree) {
          const elapsed = (Date.now() - new Date(lastFree).getTime()) / 1000;
          setCanClaimDaily(elapsed >= 86400);
        } else if (wRes.data.credit_balance > 0 || wRes.data.unlocked_episodes?.length > 0) {
          setCanClaimDaily(true);
        }
      } catch (e) {}
      const seriesSlug = episode?.series || 'the-red-ink-chronicles';
      const storageKey = `ris_free_episodes_${seriesSlug}`;
      const freeUsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
      setFreeEpisodesUsed(freeUsed);
      if (freeUsed.includes(id)) {
        setHasAccess(true);
      } else if (freeUsed.length < FREE_LIMIT) {
        const updated = [...freeUsed, id];
        localStorage.setItem(storageKey, JSON.stringify(updated));
        setFreeEpisodesUsed(updated);
        setHasAccess(true);
      } else {
        setIsGated(true);
      }
    };
    if (episode) checkAccess();
  }, [episode, id]);

  const redeemWithCredits = async () => {
    setRedeeming(true);
    try {
      const userToken = getUserToken();
      const res = await axios.post(`${API}/store/redeem/episode`, { user_token: userToken, episode_id: id });
      if (res.data.success) {
        setWallet(prev => ({ ...prev, credit_balance: res.data.credit_balance, unlocked_episodes: [...(prev?.unlocked_episodes || []), id] }));
        setHasAccess(true);
        setIsGated(false);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Redemption failed');
    } finally { setRedeeming(false); }
  };

  const claimDailyFree = async () => {
    setRedeeming(true);
    try {
      const userToken = getUserToken();
      const res = await axios.post(`${API}/store/daily-free`, { user_token: userToken, episode_id: id });
      if (res.data.success) {
        setWallet(prev => ({ ...prev, daily_free_episodes: [...(prev?.daily_free_episodes || []), id] }));
        setHasAccess(true);
        setIsGated(false);
        setCanClaimDaily(false);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not claim daily free episode');
    } finally { setRedeeming(false); }
  };

  // Play / pause this episode via global engine
  const togglePlayPause = () => {
    if (!episode) return;
    if (isThisPlaying) {
      togglePlay();
    } else {
      const t = buildTrack(episode);
      const next = adjacentEps.next ? buildTrack(adjacentEps.next) : null;
      if (t) playTrack(t, next);
    }
  };

  const seekTo = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    if (!isThisPlaying) {
      // Start playing this episode first, then seek after metadata loads
      const t = buildTrack(episode);
      const next = adjacentEps.next ? buildTrack(adjacentEps.next) : null;
      if (t) {
        playTrack(t, next);
        // queue seek for next tick
        setTimeout(() => {
          const dur = ctxDuration;
          if (dur && isFinite(dur)) seek(pct * dur);
        }, 250);
      }
      return;
    }
    if (audioDuration) seek(pct * audioDuration);
  };

  const onSeekMouseDown = (e) => {
    setIsDraggingSeek(true);
    seekTo(e);
  };

  useEffect(() => {
    if (!isDraggingSeek) return;
    const onMove = (e) => {
      const bar = document.querySelector('[data-testid="audio-seek-bar"]');
      if (!bar || !audioDuration) return;
      const rect = bar.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      seek(pct * audioDuration);
    };
    const onUp = () => setIsDraggingSeek(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [isDraggingSeek, audioDuration, seek]);

  const skipBy = (deltaSec) => {
    setSkipFlash(deltaSec < 0 ? 'back' : 'fwd');
    setTimeout(() => setSkipFlash(null), 350);
    if (!isThisPlaying) {
      const t = buildTrack(episode);
      const next = adjacentEps.next ? buildTrack(adjacentEps.next) : null;
      if (t) {
        playTrack(t, next);
        setTimeout(() => ctxSkipBy(deltaSec), 300);
      }
      return;
    }
    ctxSkipBy(deltaSec);
  };

  // Keyboard shortcuts: space=play/pause, ←/→ = -10/+10
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.code === 'Space') { e.preventDefault(); togglePlayPause(); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); skipBy(-10); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); skipBy(10); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.id, isThisPlaying]);

  const handleDownload = async () => {
    if (!episode) return;
    const audioUrl = episode.mixed_audio_url || episode.audio_url;
    if (!audioUrl) return;
    const filename = `S${episode.season}E${String(episode.episode_number).padStart(2,'0')} - ${episode.title.replace(/[^a-zA-Z0-9 '-]/g, '')}.mp3`;
    // Backend supports ?download=1 → adds Content-Disposition: attachment
    const downloadUrl = `${BACKEND_URL}${audioUrl}${audioUrl.includes('?') ? '&' : '?'}download=1`;

    // Strategy 1: blob fetch (cross-origin safe, forces save dialog)
    try {
      const resp = await fetch(downloadUrl);
      if (resp.ok) {
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(blobUrl);
        }, 1500);
        return;
      }
    } catch (e) {
      console.error('Blob download failed, falling back:', e);
    }

    // Strategy 2: direct navigation to download URL (browser handles save)
    window.location.href = downloadUrl;
  };

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/episodes/${id}` : '';
  const shareTitle = episode ? `${seriesLabelFor(episode.series)} — S${episode.season}E${episode.episode_number}: ${episode.title}` : '';
  const shareText = episode ? `Listen to "${episode.title}" — ${episode.logline || 'a Red Ink Studios audio drama.'}` : '';

  const openShare = (platform) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareTitle);
    const text = encodeURIComponent(shareText);
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      reddit: `https://www.reddit.com/submit?url=${u}&title=${t}`,
      whatsapp: `https://wa.me/?text=${text}%20${u}`,
      email: `mailto:?subject=${t}&body=${text}%0A%0A${u}`,
    };
    if (map[platform]) window.open(map[platform], '_blank', 'noopener,noreferrer,width=720,height=520');
  };

  // Native Web Share API (mobile + supported desktop browsers)
  const nativeShare = async () => {
    if (!navigator.share) return false;
    try {
      await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
      return true;
    } catch (e) {
      // User cancelled or unsupported — fall back to popover
      return false;
    }
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 1800);
    } catch {
      // Fallback: textarea trick
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopyOk(true); setTimeout(() => setCopyOk(false), 1800); } catch {}
      document.body.removeChild(ta);
    }
  };

  // Admin-only: copy the raw script text cleanly (instead of selecting the
  // styled, line-split render which pastes as a messy dump).
  const copyStoryText = async () => {
    const text = episode?.full_story || episode?.script || '';
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setStoryCopied(true);
    setTimeout(() => setStoryCopied(false), 1800);
  };
  const toggleShare = async () => {
    if (!shareOpen) {
      const handled = await nativeShare();
      if (handled) return;
    }
    setShareOpen(o => !o);
  };

  // Close share popover when clicking outside
  useEffect(() => {
    if (!shareOpen) return;
    const onDocClick = (e) => {
      if (!e.target.closest('[data-testid="share-popover"]') && !e.target.closest('[data-testid="share-episode-btn"]')) {
        setShareOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [shareOpen]);

  const formatTime = (t) => {
    if (!t || isNaN(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (<div className="min-h-screen pt-24 flex items-center justify-center"><div className="w-12 h-12 border-2 border-red-600 border-t-transparent rounded-full animate-spin" /></div>);
  }
  if (!episode) {
    return (<div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-gray-500">Episode not found</p></div>);
  }

  const audioUrl = episode.mixed_audio_url || episode.audio_url;
  const hasAudio = !!audioUrl;
  const seriesLabel = seriesLabelFor(episode.series);

  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="episode-detail-page">
      {/* Hero Banner */}
      <div className="relative h-56 md:h-80">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${episode.thumbnail_url ? `${BACKEND_URL}${episode.thumbnail_url}` : COVER_IMAGE})` }} />
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent' : 'hero-overlay'}`} />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          <div className="max-w-3xl mx-auto">
            <p className="font-mono text-red-500 text-xs tracking-widest mb-1">{seriesLabel.toUpperCase()}</p>
            <div className="flex items-center gap-3 mb-2">
              <p className="font-mono text-red-500/70 text-sm tracking-widest">SEASON {episode.season} &bull; EPISODE {episode.episode_number}</p>
            </div>
            <h1 className="font-cinzel text-2xl md:text-4xl font-bold mb-2">{episode.title}</h1>
            <p className={`text-sm max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{episode.logline}</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6">
        {/* Episode Navigation */}
        <div className="flex items-center justify-between py-4 border-b border-white/5" data-testid="episode-navigation">
          {adjacentEps.prev ? (
            <button onClick={() => navigate(`/episodes/${adjacentEps.prev.id}`)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`} data-testid="prev-episode-btn">
              <ChevronLeft size={16} />
              <span className="hidden sm:inline">E{adjacentEps.prev.episode_number}: {adjacentEps.prev.title?.substring(0, 25)}{adjacentEps.prev.title?.length > 25 ? '...' : ''}</span>
              <span className="sm:hidden">Prev</span>
            </button>
          ) : <div />}
          <button onClick={() => navigate(`/series/${episode.series}/episodes?season=${episode.season}`)} className={`px-3 py-2 rounded-lg text-xs font-mono transition-all ${isDark ? 'hover:bg-white/5 text-gray-500' : 'hover:bg-gray-100 text-gray-500'}`} data-testid="back-to-episodes-btn">
            All Episodes
          </button>
          {adjacentEps.next ? (
            <button onClick={() => navigate(`/episodes/${adjacentEps.next.id}`)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`} data-testid="next-episode-btn">
              <span className="hidden sm:inline">E{adjacentEps.next.episode_number}: {adjacentEps.next.title?.substring(0, 25)}{adjacentEps.next.title?.length > 25 ? '...' : ''}</span>
              <span className="sm:hidden">Next</span>
              <ChevronRight size={16} />
            </button>
          ) : <div />}
        </div>

        {/* Content Gate */}
        {isGated && (
          <div className="my-8" data-testid="content-gate">
            <div className="relative overflow-hidden" style={{ maxHeight: '300px' }}>
              <p className={`leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {(episode.full_story || episode.script || episode.summary || '').substring(0, 800)}...
              </p>
              <div className={`absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t ${isDark ? 'from-[#050505]' : 'from-white'} to-transparent`} />
            </div>
            <div className={`mt-4 p-8 border ${isDark ? 'border-red-500/30 bg-red-950/20' : 'border-red-200 bg-red-50'} rounded-lg text-center`}>
              <Lock size={28} className="mx-auto mb-3 text-red-500" />
              <h3 className="font-cinzel text-xl font-bold mb-2">Continue Reading</h3>
              <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You've used your {FREE_LIMIT} free episodes for this series.</p>
              <p className={`text-sm mb-6 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                Unlock for <span className="text-red-500 font-bold">{creditCost} credits</span>{wallet ? ` (${wallet.credit_balance || 0} credits available)` : ''}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {canClaimDaily && (
                  <button onClick={claimDailyFree} disabled={redeeming} className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 font-bold rounded transition-colors text-sm" data-testid="gate-daily-free-btn">
                    {redeeming ? 'Claiming...' : 'Use Daily Free Unlock'}
                  </button>
                )}
                {wallet && (wallet.credit_balance || 0) >= creditCost ? (
                  <button onClick={redeemWithCredits} disabled={redeeming} className="btn-primary px-6 py-2.5 text-sm" data-testid="gate-redeem-btn">
                    {redeeming ? 'Unlocking...' : <><Unlock size={16} /> Unlock for {creditCost} Credits</>}
                  </button>
                ) : (
                  <button onClick={() => navigate('/store')} className="btn-primary px-6 py-2.5 text-sm" data-testid="gate-buy-credits-btn">
                    <Coins size={16} /> Buy Credits
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!isGated && hasAccess && freeEpisodesUsed.length > 0 && freeEpisodesUsed.length < FREE_LIMIT && (
          <div className={`my-4 p-2.5 rounded-lg text-center text-xs ${isDark ? 'bg-white/5 text-gray-400' : 'bg-gray-100 text-gray-600'}`} data-testid="free-counter">
            {FREE_LIMIT - freeEpisodesUsed.length} free episode{FREE_LIMIT - freeEpisodesUsed.length !== 1 ? 's' : ''} remaining
          </div>
        )}

        {/* Main Content (unlocked) */}
        {!isGated && (
          <>
            {/* Audio Player with hero artwork — ALWAYS rendered.
                When the episode has no audio yet (audio_url null/empty), the widget
                still renders but the play button is disabled and a "Audio not yet
                available" badge is shown over the artwork so users always have a
                visible player affordance. */}
            <div className={`my-6 rounded-xl border ${isDark ? 'bg-[#0c0c0c] border-white/10' : 'bg-gray-50 border-gray-200'} overflow-visible`} data-testid="episode-audio-player">
                {/* Hero Artwork */}
                <div className="relative w-full overflow-hidden rounded-t-xl" style={{ aspectRatio: '21 / 9' }} data-testid="episode-hero-art">
                  <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                      backgroundImage: `url(${episode.thumbnail_url ? `${BACKEND_URL}${episode.thumbnail_url}` : COVER_IMAGE})`,
                      transform: localIsPlaying ? 'scale(1.04)' : 'scale(1)',
                      transition: 'transform 0.6s ease-out',
                    }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${isDark ? 'from-[#0c0c0c]' : 'from-white'} via-transparent to-black/30`} />
                  {/* Episode info overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                    <p className="font-mono text-[10px] sm:text-xs text-red-400 tracking-[0.2em] mb-1">
                      {seriesLabel.toUpperCase()} · S{episode.season}E{String(episode.episode_number).padStart(2,'0')}
                    </p>
                    <h2 className={`font-cinzel text-xl sm:text-2xl md:text-3xl font-bold leading-tight ${isDark ? 'text-white' : 'text-white drop-shadow-lg'}`} data-testid="hero-art-title">
                      {episode.title}
                    </h2>
                  </div>
                  {/* Big play overlay button */}
                  <button
                    onClick={togglePlayPause}
                    disabled={!hasAudio}
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-2xl transition-all ${
                      hasAudio
                        ? `bg-red-600 hover:bg-red-700 shadow-red-900/60 ring-4 ring-red-500/30 hover:scale-105 ${localIsPlaying ? 'opacity-0 pointer-events-none' : 'opacity-100'}`
                        : 'bg-gray-700/70 cursor-not-allowed ring-4 ring-gray-500/20 opacity-80'
                    }`}
                    data-testid="hero-play-btn"
                    title={hasAudio ? (localIsPlaying ? 'Pause' : 'Play') : 'Audio not yet available'}
                  >
                    <Play size={32} className={`${hasAudio ? 'text-white' : 'text-gray-300'} ml-1`} />
                  </button>
                  {/* Audio-not-available badge */}
                  {!hasAudio && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/15 text-[11px] font-mono tracking-wider text-amber-300 flex items-center gap-1.5" data-testid="audio-pending-badge">
                      <Volume2 size={12} />
                      AUDIO PRODUCTION IN PROGRESS
                    </div>
                  )}
                  {/* Resume hint */}
                  {!localIsPlaying && episode && getPosition && (() => {
                    const saved = getPosition(episode.id);
                    if (saved < 5) return null;
                    const m = Math.floor(saved / 60);
                    const s = String(Math.floor(saved % 60)).padStart(2, '0');
                    return (
                      <div className="absolute top-3 left-3 px-2 py-1 rounded bg-black/60 backdrop-blur-sm border border-white/10 text-[10px] font-mono text-white" data-testid="resume-hint">
                        ▸ Resume from {m}:{s}
                      </div>
                    );
                  })()}
                  {/* Subtle equalizer bars when playing */}
                  {localIsPlaying && (
                    <div className="absolute top-3 right-3 flex items-end gap-0.5 h-5" data-testid="hero-equalizer">
                      <span className="w-0.5 h-2 bg-red-400 animate-eq-bar-1" />
                      <span className="w-0.5 h-4 bg-red-400 animate-eq-bar-2" />
                      <span className="w-0.5 h-3 bg-red-400 animate-eq-bar-3" />
                      <span className="w-0.5 h-5 bg-red-400 animate-eq-bar-2" />
                      <span className="w-0.5 h-2 bg-red-400 animate-eq-bar-1" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {/* Rewind 10s */}
                    <button
                      onClick={() => skipBy(-10)}
                      disabled={!hasAudio}
                      className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 transition-all ${!hasAudio ? 'bg-gray-300/40 text-gray-500 cursor-not-allowed' : skipFlash === 'back' ? 'bg-red-600 text-white scale-95 ring-2 ring-red-400' : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')}`}
                      data-testid="skip-back-10-btn"
                      title="Back 10 seconds (←)"
                    >
                      <Rewind size={16} />
                    </button>

                    {/* Play Button */}
                    <button
                      onClick={togglePlayPause}
                      disabled={!hasAudio}
                      className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-colors shadow-lg ${hasAudio ? 'bg-red-600 hover:bg-red-700 shadow-red-900/30' : 'bg-gray-500/40 cursor-not-allowed shadow-none'}`}
                      data-testid="play-pause-btn"
                      title={hasAudio ? (localIsPlaying ? 'Pause' : 'Play') : 'Audio not yet available'}
                    >
                      {localIsPlaying ? <Pause size={22} className="text-white" /> : <Play size={22} className={`${hasAudio ? 'text-white' : 'text-gray-300'} ml-0.5`} />}
                    </button>

                    {/* Forward 10s */}
                    <button
                      onClick={() => skipBy(10)}
                      disabled={!hasAudio}
                      className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 transition-all ${!hasAudio ? 'bg-gray-300/40 text-gray-500 cursor-not-allowed' : skipFlash === 'fwd' ? 'bg-red-600 text-white scale-95 ring-2 ring-red-400' : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')}`}
                      data-testid="skip-fwd-10-btn"
                      title="Forward 10 seconds (→)"
                    >
                      <FastForward size={16} />
                    </button>

                    {/* Progress */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {!hasAudio ? 'Audio not yet available' : (episode.mixed_audio_url ? 'Full Production Mix' : 'Audio')}
                        </span>
                        <span className="text-[10px] text-gray-500 font-mono">
                          {formatTime(currentTime)} / {formatTime(audioDuration)}
                        </span>
                      </div>
                      <div
                        className={`h-2.5 rounded-full relative group ${isDark ? 'bg-white/10' : 'bg-gray-200'} ${hasAudio ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                        onMouseDown={hasAudio ? onSeekMouseDown : undefined}
                        onTouchStart={hasAudio ? onSeekMouseDown : undefined}
                        data-testid="audio-seek-bar"
                      >
                        <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${audioDuration ? (currentTime / audioDuration) * 100 : 0}%` }} />
                        <div className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md" style={{ left: `calc(${audioDuration ? (currentTime / audioDuration) * 100 : 0}% - 7px)` }} />
                      </div>
                    </div>
                  </div>

                  {/* Mobile-only skip buttons row */}
                  <div className="flex sm:hidden items-center justify-center gap-3 mt-3">
                    <button onClick={() => skipBy(-10)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${skipFlash === 'back' ? 'bg-red-600 text-white scale-95' : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')}`} data-testid="skip-back-10-mobile-btn">
                      <Rewind size={14} /> 10s
                    </button>
                    <button onClick={() => skipBy(10)} className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${skipFlash === 'fwd' ? 'bg-red-600 text-white scale-95' : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')}`} data-testid="skip-fwd-10-mobile-btn">
                      10s <FastForward size={14} />
                    </button>
                  </div>

                  {/* Controls Row */}
                  <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      {/* Download (admin only) */}
                      {isAdmin && (
                        <button
                          onClick={handleDownload}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isDark ? 'bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-300'}`}
                          data-testid="download-episode-btn"
                          title="Admin only — download master audio"
                        >
                          <Download size={14} />
                          Download
                        </button>
                      )}

                      {/* Share */}
                      <div className="relative">
                        <button
                          onClick={toggleShare}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                          data-testid="share-episode-btn"
                        >
                          <Share2 size={14} />
                          Share
                        </button>
                        {shareOpen && (
                          <div className={`absolute z-20 mt-2 left-0 w-64 p-3 rounded-lg shadow-xl border ${isDark ? 'bg-[#0c0c0c] border-white/10' : 'bg-white border-gray-200'}`} data-testid="share-popover">
                            {/* Copy link — universal, always works */}
                            <button
                              onClick={copyShareLink}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded text-xs font-bold transition-colors mb-2 ${
                                copyOk
                                  ? 'bg-green-600/20 text-green-400 border border-green-500/30'
                                  : isDark ? 'bg-red-600/15 hover:bg-red-600/25 text-red-300 border border-red-500/30' : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-300'
                              }`}
                              data-testid="share-copy-link-btn"
                            >
                              {copyOk ? <Check size={14} /> : <Link2 size={14} />}
                              {copyOk ? 'Link copied!' : 'Copy share link'}
                            </button>
                            <p className={`text-[10px] font-mono tracking-widest mb-2 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>OR SHARE TO</p>
                            <div className="grid grid-cols-5 gap-1">
                              <button
                                onClick={() => { openShare('twitter'); setShareOpen(false); }}
                                className={`flex flex-col items-center gap-1 px-1 py-2 rounded text-[10px] ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                data-testid="share-twitter-btn"
                                title="Share on X / Twitter"
                              >
                                <Twitter size={16} className="text-sky-400" />
                                <span>X</span>
                              </button>
                              <button
                                onClick={() => { openShare('facebook'); setShareOpen(false); }}
                                className={`flex flex-col items-center gap-1 px-1 py-2 rounded text-[10px] ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                data-testid="share-facebook-btn"
                                title="Share on Facebook"
                              >
                                <Facebook size={16} className="text-blue-500" />
                                <span>Facebook</span>
                              </button>
                              <button
                                onClick={() => { openShare('whatsapp'); setShareOpen(false); }}
                                className={`flex flex-col items-center gap-1 px-1 py-2 rounded text-[10px] ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                data-testid="share-whatsapp-btn"
                                title="Share on WhatsApp"
                              >
                                <MessageCircle size={16} className="text-green-500" />
                                <span>WhatsApp</span>
                              </button>
                              <button
                                onClick={() => { openShare('reddit'); setShareOpen(false); }}
                                className={`flex flex-col items-center gap-1 px-1 py-2 rounded text-[10px] ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                data-testid="share-reddit-btn"
                                title="Share on Reddit"
                              >
                                <Share2 size={16} className="text-orange-500" />
                                <span>Reddit</span>
                              </button>
                              <button
                                onClick={() => { openShare('email'); setShareOpen(false); }}
                                className={`flex flex-col items-center gap-1 px-1 py-2 rounded text-[10px] ${isDark ? 'hover:bg-white/5 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                                data-testid="share-email-btn"
                                title="Share via Email"
                              >
                                <Mail size={16} className="text-gray-400" />
                                <span>Email</span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Next Episode shortcut */}
                      {adjacentEps.next && (
                        <button
                          onClick={() => navigate(`/episodes/${adjacentEps.next.id}`)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                          data-testid="next-episode-shortcut"
                        >
                          <SkipForward size={14} />
                          Next
                        </button>
                      )}

                      {/* Playback Speed */}
                      <button
                        onClick={cyclePlaybackRate}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all min-w-[3.5rem] ${playbackRate !== 1 ? 'bg-red-600/20 text-red-400 border border-red-500/30' : (isDark ? 'bg-white/5 hover:bg-white/10 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700')}`}
                        data-testid="playback-speed-btn"
                        title="Click to cycle playback speed (0.75x → 2x)"
                      >
                        <span className="font-mono">{playbackRate}x</span>
                      </button>
                    </div>

                    {/* Auto-play Next Toggle */}
                    <button
                      onClick={toggleAutoPlayNext}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        autoPlayNext
                          ? 'bg-red-600/20 text-red-400 border border-red-500/30'
                          : isDark ? 'bg-white/5 text-gray-500 border border-white/5' : 'bg-gray-100 text-gray-400 border border-gray-200'
                      }`}
                      data-testid="autoplay-toggle"
                    >
                      <div className={`w-8 h-4 rounded-full relative transition-colors ${autoPlayNext ? 'bg-red-600' : isDark ? 'bg-gray-700' : 'bg-gray-300'}`}>
                        <div className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform shadow-sm"
                          style={{ transform: autoPlayNext ? 'translateX(16px)' : 'translateX(0)' }}
                        />
                      </div>
                      Auto-play
                    </button>
                  </div>
                </div>
              </div>

            {/* No Audio Message removed — the audio widget itself now degrades gracefully when no audio is present (always renders with disabled controls + "AUDIO PRODUCTION IN PROGRESS" badge). */}

            {/* Summary */}
            <section className="my-8">
              <h2 className="font-anton text-lg text-red-500 mb-3">SUMMARY</h2>
              <p className={`leading-relaxed text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{episode.summary}</p>
            </section>

            {/* Characters & Locations */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <section>
                <h2 className="font-anton text-sm text-red-500 mb-2">CHARACTERS</h2>
                <div className="flex flex-wrap gap-1.5">
                  {episode.characters?.map((char) => (<span key={char} className={`px-2.5 py-1 text-xs rounded ${isDark ? 'bg-white/5 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>{char}</span>))}
                </div>
              </section>
              <section>
                <h2 className="font-anton text-sm text-red-500 mb-2">LOCATIONS</h2>
                <div className="flex flex-wrap gap-1.5">
                  {episode.locations?.map((loc) => (<span key={loc} className="px-2.5 py-1 bg-cyan-500/10 text-cyan-600 text-xs rounded">{loc}</span>))}
                </div>
              </section>
            </div>

            {/* Readable Story */}
            {(episode.full_story || episode.script) && (
              <section className="mb-8">
                <button
                  onClick={() => setShowStory(!showStory)}
                  className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${isDark ? 'bg-white/5 hover:bg-white/8' : 'bg-gray-100 hover:bg-gray-200'}`}
                  data-testid="toggle-story-btn"
                >
                  <div className="flex items-center gap-2.5">
                    <BookOpen size={18} className="text-red-500" />
                    <span className="font-anton text-lg text-red-500">READ STORY</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {episode.word_count > 0 && (
                      <span className={`text-xs font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{Math.ceil(episode.word_count / 200)} min read</span>
                    )}
                    {showStory ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
                  </div>
                </button>

                {showStory && (
                  <div className={`mt-3 p-6 rounded-lg border ${isDark ? 'bg-[#0a0a0a] border-white/5' : 'bg-white border-gray-200'}`} data-testid="story-content">
                    {isAdmin && (
                      <div className="flex justify-end mb-4">
                        <button
                          onClick={copyStoryText}
                          data-testid="copy-script-btn"
                          title="Copy the raw script to paste elsewhere"
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${storyCopied ? 'bg-green-600 text-white' : isDark ? 'bg-white/10 hover:bg-white/15 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                        >
                          {storyCopied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy Script</>}
                        </button>
                      </div>
                    )}
                    {episode.full_story ? (
                      <div className="space-y-1">
                        {episode.full_story.split('\n').map((line, i) => {
                          if (line.trim().match(/^\[SFX:/i)) return <div key={i} className="text-cyan-400 italic my-2 text-sm">{line}</div>;
                          if (line.trim().startsWith('[Scene:') || line.trim().startsWith('[')) return <div key={i} className="text-purple-400 italic my-3 text-sm">{line}</div>;
                          const charMatch = line.match(/^([A-Z][A-Z\-\.Λ]+):/);
                          if (charMatch) return (<div key={i} className="my-2 text-sm"><span className="text-yellow-500 font-semibold">{charMatch[0]}</span><span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{line.substring(charMatch[0].length)}</span></div>);
                          if (line.includes('*') && line.indexOf('*') !== line.lastIndexOf('*')) return <div key={i} className="text-gray-400 italic my-1 text-sm">{line}</div>;
                          if (line.trim()) return <div key={i} className={`my-1 text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{line}</div>;
                          return <div key={i} className="h-2"></div>;
                        })}
                      </div>
                    ) : (
                      <div>
                        <div className={`flex items-center justify-between mb-4 pb-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <span className="text-yellow-500 text-sm">Original Outline</span>
                        </div>
                        {episode.script.split('\n').map((line, i) => {
                          if (line.trim().startsWith('SFX:')) return <div key={i} className="text-cyan-400 italic text-sm">{line}</div>;
                          if (line.trim().match(/^(SCENE|INT\.|EXT\.)/i)) return <div key={i} className="text-red-500 font-bold mt-4 text-sm">{line}</div>;
                          const charMatch = line.match(/^([A-Z][A-Z\-\.Λ]+)(\s*\([^)]+\))?:/);
                          if (charMatch) return (<div key={i} className="text-sm"><span className="text-yellow-500 font-bold">{charMatch[0]}</span><span className={isDark ? 'text-gray-200' : 'text-gray-800'}>{line.substring(charMatch[0].length)}</span></div>);
                          if (line.trim().startsWith('END OF')) return <div key={i} className="text-red-500 font-bold mt-4 text-center text-sm">{line}</div>;
                          return <div key={i} className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{line}</div>;
                        })}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
